// The hero portrait, which can be replaced from the admin.
//
// Nothing is rendered until we know which picture to show. Committing to the
// placeholder first and swapping when the content request landed was the
// visible flicker: the URL only exists inside that response, so the fallback
// always painted first and was always replaced a moment later.

import { computed, ref, watch } from 'vue';

const BUILT_IN = '/images/profile.svg';

// Twice the rendered width, so it stays sharp on a retina screen. The stored
// master is full-width, which was 462KB for a frame about 300px across.
const DELIVERY_WIDTH = 700;

export function useAvatar() {
  const { useSettings, usePending } = useLiveContent();
  const settings = useSettings();
  const pending = usePending();

  // Three sources, tried in order: the resized delivery, the stored original,
  // then the committed placeholder.
  const stage = ref(0);
  const loaded = ref(false);

  const chosen = computed(() => settings.value.avatarUrl || null);

  watch(chosen, () => {
    stage.value = 0;
    loaded.value = false;
  });

  // Only applied to blobs the admin uploaded; the placeholder is a committed
  // SVG with no raster size to reduce.
  function resized(url) {
    if (!url.startsWith('/.netlify/functions/media')) return url;
    return `/.netlify/images?url=${encodeURIComponent(url)}&w=${DELIVERY_WIDTH}&fm=webp&q=76`;
  }

  // Null while the content request is still out, so no <img> is rendered at all
  // and the skeleton holds the frame.
  const src = computed(() => {
    if (pending.value) return null;
    const url = chosen.value;
    if (!url || stage.value >= 2) return BUILT_IN;
    return stage.value === 0 ? resized(url) : url;
  });

  // The skeleton covers both waits: deciding which picture, then loading it.
  const ready = computed(() => !pending.value && loaded.value);

  // Only `error` advances the chain, and a request that hangs never fires one,
  // which would leave the skeleton up for the life of the page. Generous, since
  // falling back early would show the placeholder when the real picture was
  // merely slow.
  const STALL_MS = 8000;
  let timer = null;

  function arm() {
    if (typeof window === 'undefined') return;
    clearTimeout(timer);
    if (!src.value || loaded.value) return;
    timer = setTimeout(() => {
      if (!loaded.value) onError();
    }, STALL_MS);
  }

  watch(src, arm, { immediate: true });

  function onLoad() {
    clearTimeout(timer);
    loaded.value = true;
  }

  function onError() {
    clearTimeout(timer);
    if (stage.value < 2) {
      stage.value += 1;
      loaded.value = false;
      arm();
      return;
    }
    // The committed file is what failed, so there is nothing left to try.
    // Reveal anyway rather than leaving the skeleton up for ever.
    loaded.value = true;
  }

  return { src, ready, onLoad, onError };
}
